// PartnershipsPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, Grid, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const PartnershipsPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Partnerships', path: '/sustainability/partnerships' },
  ];

  const partnershipTypes = [
    {
      title: 'Industry Collaborations',
      description: 'We partner with leading industry associations, recycling organizations, and manufacturing companies to drive innovation and best practices in material recovery and reuse.',
      examples: [
        'Collaborating on industry standards for scrap quality.',
        'Joint research and development on new recycling technologies.',
        'Participating in forums to shape sustainable policies.',
      ],
    },
    {
      title: 'Academic & Research Partnerships',
      description: 'Working with universities and research institutions, we explore cutting-edge solutions for material science, waste reduction, and environmental impact assessment.',
      examples: [
        'Sponsoring research projects on sustainable materials.',
        'Providing data for academic studies on circular economy models.',
        'Mentoring students interested in green industries.',
      ],
    },
    {
      title: 'Non-Profit & Community Alliances',
      description: 'Our alliances with non-profit organizations and local communities help us extend our reach, promote environmental education, and support grassroots sustainability initiatives.',
      examples: [
        'Joint campaigns for public awareness on recycling.',
        'Support for community clean-up drives.',
        'Funding educational programs on waste management.',
      ],
    },
    {
      title: 'Technology & Innovation Partners',
      description: 'We collaborate with technology providers to enhance our platform\'s capabilities, from advanced sorting and processing solutions to data analytics for market insights.',
      examples: [
        'Integrating AI/ML for scrap identification and grading.',
        'Developing blockchain solutions for supply chain transparency.',
        'Implementing IoT for real-time inventory management.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Partnerships
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Collaboration is key to achieving a truly sustainable future. We actively seek and foster partnerships with organizations across various sectors to amplify our impact and drive collective progress in recycling and resource management.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {partnershipTypes.map((partnership, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {partnership.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {partnership.description}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Examples of Our Work:
                    </Typography>
                    <List dense disablePadding>
                      {partnership.examples.map((example, exampleIndex) => (
                        <ListItem key={exampleIndex} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText primary={example} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default PartnershipsPage;
